import { Flex } from 'antd';
import styles from './ProductVariantNameBox.module.scss';
import CircularPill from 'components/lead-details-common/circularPill';
import { ChevronDownIcon, OrderIcon } from 'common/all-icons/custom-icons';
import ProductVariantDropdown from './ProductVariantDropdown';
import clsx from 'clsx';
import { isSmallScreen } from 'utils';
import { useState } from 'react';
import TruncateText from 'components/truncate-text';
import { IF } from 'common';

const ProductVariantItem = ({ variant = {}, darkTheme = false, productClassName = '' }) => {
  const isInternal = variant?.product?.type?.slug === 'internal';
  const variantName = variant?.name || '';
  const productName = variant?.product?.name;
  const productNameLimit = 25;
  const limit = isInternal ? productNameLimit : 60;
  const isTruncated = variantName.length > limit || (isInternal ? productName.length > productNameLimit : false);

  return (
    <ProductVariantDropdown variants={!isTruncated ? [] : [variant]} darkTheme={darkTheme}>
      <Flex className={clsx(styles.box, productClassName)} align="center" gap={12}>
        <OrderIcon width={24} height={24} style={{ flexShrink: 0 }} className="primary-text" />

        <Flex vertical>
          <IF condition={isInternal}>
            <TruncateText
              text={productName}
              textClassName="body-12-semibold"
              limit={productNameLimit}
              showTooltip={false}
            />
          </IF>

          <TruncateText
            text={variantName}
            style={{ color: isInternal ? 'var(--user-main-text)' : '' }}
            textClassName={clsx(isInternal ? 'body-11-medium' : 'body-12-semibold')}
            limit={limit}
            showTooltip={false}
          />
        </Flex>
      </Flex>
    </ProductVariantDropdown>
  );
};

const ProductVariantNameBox = ({
  productVariants = [],
  productsToShow = 2,
  isExpandOnClick = false,
  darkTheme = false,
  dropdownTrigger = ['hover'],
  attachDropdownToBody = true,
  productClassName = '',
  hidePill = false,
}) => {
  const [displayProducts, setDisplayProducts] = useState(productsToShow);

  if (!productVariants?.length) {
    return (
      <Flex className={styles.box} align="center" gap={12}>
        <OrderIcon width={24} height={24} style={{ flexShrink: 0 }} className="primary-text" />
        <div className="body-12-semibold">No Product Found</div>
      </Flex>
    );
  }

  const visibleProducts = productVariants?.slice(0, displayProducts);
  const hiddenProducts = productVariants?.slice(displayProducts);

  const remainingVariants = hiddenProducts?.length;
  const showTooltip = !!remainingVariants;

  return (
    <Flex align="stretch" gap={12} wrap>
      {visibleProducts?.map((variant) => (
        <ProductVariantItem
          key={variant?.id}
          variant={variant}
          darkTheme={darkTheme}
          productClassName={productClassName}
        />
      ))}

      {showTooltip && (
        <Flex vertical justify="center">
          <ProductVariantDropdown
            variants={isExpandOnClick ? [] : productVariants?.slice(productsToShow)}
            darkTheme={darkTheme}
            trigger={dropdownTrigger}
            attachToBody={attachDropdownToBody}
          >
            <IF
              condition={productsToShow}
              ELSE={
                <Flex className={clsx(hidePill ? styles.productCountWrap : styles.orderIcon)} gap={4} align="center">
                  <OrderIcon className="primary-text" />
                  <IF
                    condition={!hidePill}
                    ELSE={
                      <Flex align="center" gap={4}>
                        <div className="body-12-semibold">{remainingVariants} Products</div>
                        <ChevronDownIcon width={16} height={16} className="primary-text" />
                      </Flex>
                    }
                  >
                    <CircularPill
                      role="button"
                      className="body-11-medium"
                      onClick={() => isExpandOnClick && setDisplayProducts(productVariants.length)}
                      size={20}
                      value={`+${remainingVariants}`}
                    />
                  </IF>
                </Flex>
              }
            >
              <CircularPill
                role="button"
                className="body-14-semibold body-12-semibold-small"
                onClick={() => isExpandOnClick && setDisplayProducts(productVariants.length)}
                size={isSmallScreen() ? 28 : 32}
                value={`+${remainingVariants}`}
              />
            </IF>
          </ProductVariantDropdown>
        </Flex>
      )}
    </Flex>
  );
};

export default ProductVariantNameBox;
